import { useState } from 'react';
import { useDefaultFormErrorNotification } from '@/hooks';
import {
  Button,
  Col,
  Form,
  message,
  notification,
  Row,
  Space,
  Steps,
  Tooltip,
} from 'antd';

import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { DateTimeFormat } from '@/utils/moment';
import {
  useCreateWorkOrder,
  useWorkOrderCreateByPorto,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { AxiosError } from 'axios';
import {
  CustomerType,
  WorkOrderCreateByPolicyCompanyCode,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { WorkOrderComplementType } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { removeMask } from '@/utils/mask';
import { KanbanSchedulePortoFormValues } from './form.types';
import KanbanSchedulePortoFormScheduleFields from './ScheduleFields';
import KanbanSchedulePortoFormScheduleDateFields from './ScheduleDateFields';
import { useHistory } from 'react-router-dom';

interface Props {
  onCancel: VoidFunction;
  modalOpened?: boolean;
}

export default function KanbanSchedulePortoForm({ onCancel }: Props) {
  const history = useHistory();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  const [form] = Form.useForm<KanbanSchedulePortoFormValues>();

  const hour = Form.useWatch(['scheduleDate', 'hour'], form);

  const [step, setStep] = useState(0);

  const handleClose = () => {
    form.resetFields();
    setStep(0);
    onCancel();
  };

  const createWorkOrder = useCreateWorkOrder();
  const workOrderCreateByPolicy = useWorkOrderCreateByPorto();

  const isLoading =
    createWorkOrder.isLoading || workOrderCreateByPolicy.isLoading;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const { policy, voucher, hasVoucher, ...generalValues } = values.general;
      const scheduleDateValues = values.scheduleDate;

      if (
        !values.general.voucher?.customer &&
        values.general.customerId === 0
      ) {
        form.setFields([
          {
            name: 'customerId',
            value: undefined,
            errors: ['Informe um cliente válido'],
          },
        ]);

        return message.error('Valide os campos!');
      }

      let workOrderId: number;
      let complementId: number;

      if (policy && policy?.companyCode) {
        const result = await workOrderCreateByPolicy.mutateAsync({
          ...generalValues,
          vehicleId: generalValues.vehicle?.id || undefined,
          customerId: generalValues.customerId || undefined,
          companyCode:
            `domain.enums.company_code.${policy.companyCode.toLowerCase()}` as WorkOrderCreateByPolicyCompanyCode,
          entryDate: scheduleDateValues.entryDate
            ? scheduleDateValues.entryDate.format(DateTimeFormat)
            : undefined,
          policyNumber: policy.policyNumber as string,
          customer: policy.customer,
          vehicle: {
            ...policy.vehicle,
            licensePlate: generalValues.vehicle?.licensePlate,
            prisma: generalValues.vehicle?.prisma,
            currentKilometers: generalValues.vehicle?.currentKilometers,
            tankLevel: generalValues.vehicle?.tankLevel,
          },
          lastOilChange: generalValues.vehicle?.lastOilChange
            ? generalValues.vehicle?.lastOilChange.format(DateTimeFormat)
            : undefined,
          lastOilChangeKilometers:
            generalValues.vehicle?.lastOilChangeKilometers,
          workOrderType: WorkOrderComplementType.WorkOrder,
          customerType: CustomerType.Private,
          internalSchedule: true,
          capsCode: generalValues.capsCode,
          scheduleDuration: Number(generalValues.scheduleDuration),
          scheduledDate: scheduleDateValues.entryDate.format(
            `YYYY-MM-DD[T]${scheduleDateValues.hour}`,
          ),
        });

        workOrderId = result.workOrderId;
        complementId = result.workOrderComplementId;
      } else if (hasVoucher && !policy?.companyCode) {
        const voucherCustomer = voucher?.customer;
        const voucherAddress = voucher?.address;

        const { phone, email, ...restCustomer } = { ...voucherCustomer };

        const result = await workOrderCreateByPolicy.mutateAsync({
          ...generalValues,
          customerId:
            generalValues.customerId !== 0
              ? generalValues.customerId
              : undefined,
          entryDate: scheduleDateValues.entryDate
            ? scheduleDateValues.entryDate.format(DateTimeFormat)
            : undefined,
          customer:
            generalValues.customerId === 0
              ? {
                  ...restCustomer,
                  contacts: {
                    email: email as string,
                    phone: removeMask(phone),
                  },
                  address: voucherAddress
                    ? {
                        ...voucherAddress,
                        cep: removeMask(voucherAddress?.cep),
                      }
                    : undefined,
                }
              : undefined,
          vehicle: generalValues.vehicle
            ? {
                licensePlate: generalValues.vehicle.licensePlate,
                prisma: generalValues.vehicle?.prisma,
                currentKilometers: generalValues.vehicle?.currentKilometers,
                tankLevel: generalValues.vehicle?.tankLevel,
              }
            : undefined,
          lastOilChange: generalValues.vehicle?.lastOilChange
            ? generalValues.vehicle?.lastOilChange.format(DateTimeFormat)
            : undefined,
          lastOilChangeKilometers:
            generalValues.vehicle?.lastOilChangeKilometers,
          hasPortoVoucher: true,
          workOrderType: WorkOrderComplementType.WorkOrder,
          customerType: CustomerType.Private,
          internalSchedule: true,
          capsCode: generalValues.capsCode,
          scheduleDuration: Number(generalValues.scheduleDuration),
          scheduledDate: scheduleDateValues.entryDate.format(
            `YYYY-MM-DD[T]${scheduleDateValues.hour}`,
          ),
        });

        workOrderId = result.workOrderId;
        complementId = result.workOrderComplementId;
      } else {
        const result = await createWorkOrder.mutateAsync({
          ...generalValues,
          customerId: !!generalValues.vehicle?.id
            ? undefined
            : generalValues.customerId,
          vehicleId: generalValues.vehicle?.id || undefined,
          entryDate: scheduleDateValues.entryDate
            ? scheduleDateValues.entryDate.format(DateTimeFormat)
            : undefined,
          estimatedLeaveDate: scheduleDateValues.estimatedLeaveDate
            ? scheduleDateValues.estimatedLeaveDate.format(DateTimeFormat)
            : undefined,
          internalSchedule: true,
          capsCode: generalValues.capsCode,
          scheduleDuration: Number(generalValues.scheduleDuration),
          scheduledDate: scheduleDateValues.entryDate.format(
            `YYYY-MM-DD[T]${scheduleDateValues.hour}`,
          ),
        });

        workOrderId = result.id;
        complementId = result.complements[0].id;
      }

      history.push(
        `/app/movements/workOrders/${workOrderId}/complements/${complementId}/generalInfo`,
      );

      handleClose();
    } catch (err) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        sendFormErrorNotification(err);
      }
    }
  };

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {false && (
        <ModalLoadingOverlay
          messages={['Salvando dados...']}
          style={{ position: 'absolute', width: '100%', height: '100%' }}
        />
      )}

      <Form form={form}>
        <Steps current={step} style={{ marginBottom: '24px' }}>
          <Steps.Step title="Preenchimento do agendamento" />

          <Steps.Step title="Seleção de data e horário" />
        </Steps>

        <Row gutter={[12, 12]}>
          <Col span={24}>
            <KanbanSchedulePortoFormScheduleFields step={step} />

            <KanbanSchedulePortoFormScheduleDateFields step={step} />
          </Col>

          <Col span={24}>
            <Space style={{ width: '100%', justifyContent: 'end' }}>
              <Button onClick={handleClose}>Cancelar</Button>

              {step > 0 && step <= 1 && (
                <Button onClick={() => setStep((prev) => prev - 1)}>
                  Voltar
                </Button>
              )}

              {step < 1 && (
                <Button
                  type="primary"
                  onClick={async () => {
                    try {
                      const errors = form.getFieldsError().map((f) => f.name);

                      const generalPaths = errors.filter(
                        (error) => error.length && error[0] === 'general',
                      );

                      await form.validateFields(generalPaths);

                      setStep((prev) => prev + 1);
                    } catch {
                      notification.error({
                        message: 'Favor preencher campos obrigatórios',
                      });
                    }
                  }}
                >
                  Próximo
                </Button>
              )}

              {step === 1 && (
                <Tooltip
                  title={!hour ? 'Selecione um horário para criar' : undefined}
                >
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={isLoading}
                    disabled={!hour}
                  >
                    Criar
                  </Button>
                </Tooltip>
              )}
            </Space>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
